import { createReducer, on } from '@ngrx/store';

import moment from 'moment';
import { ExtendedRawConsumptionProperties } from '../_models';

import { FilterType } from '../../+installations/installation/energy-historical-data-modal/energy-filters-header/energy-filters.data';
import { EnergyHistoricalDataActions } from './energy-historical-data.actions';
import { filterFeaturesByFilterType, HemsFlowType } from './energy-historical-data.helpers';
import { EnergyHistoricalDataState } from './energy-historical-data.state';

export const initEnergyHistoricalDataState: EnergyHistoricalDataState = {
  chartData: null,
  filterType: FilterType.Month,
  date: moment(new Date()),
  selectedFeatures: ['ems.energy.production.total', 'ems.energy.consumption.total'],
  legendFeatures: [],
  loading: false,
  hemsAnalysisSelectedTab: HemsFlowType.Power,
  savedSelectedFeature: null,
  error: null,
};

export const energyHistoricalDataReducer = createReducer(
  initEnergyHistoricalDataState,

  on(
    EnergyHistoricalDataActions.loadChartData,
    (state): EnergyHistoricalDataState => ({
      ...state,
      loading: true,
    })
  ),

  on(
    EnergyHistoricalDataActions.loadChartDataSuccess,
    (state, { chartData }): EnergyHistoricalDataState => ({
      ...state,
      chartData,
      loading: false,
      error: null,
    })
  ),

  on(
    EnergyHistoricalDataActions.loadChartDataError,
    (state, { error }): EnergyHistoricalDataState => ({
      ...state,
      chartData: null,
      loading: false,
      error,
    })
  ),

  on(
    EnergyHistoricalDataActions.updateFilters,
    (state, { filterType, date }): EnergyHistoricalDataState => {
      const newDate = moment(date).clone();

      return {
        ...state,
        filterType,
        date: newDate,
        selectedFeatures: filterFeaturesByFilterType(state.selectedFeatures, filterType),
      };
    }
  ),

  on(
    EnergyHistoricalDataActions.updateSelectedFeatures,
    (state, { selectedFeatures }): EnergyHistoricalDataState => ({
      ...state,
      selectedFeatures,
    })
  ),

  on(
    EnergyHistoricalDataActions.resetSelectedFeatures,
    (state): EnergyHistoricalDataState => ({
      ...state,
      selectedFeatures: initEnergyHistoricalDataState.selectedFeatures,
    })
  ),

  on(
    EnergyHistoricalDataActions.addLegendFeature,
    (state, { feature }): EnergyHistoricalDataState => {
      const features = [...state.legendFeatures];

      return {
        ...state,
        legendFeatures: features.concat(feature),
      };
    }
  ),

  on(
    EnergyHistoricalDataActions.resetStore,
    (_state): EnergyHistoricalDataState => ({
      ...initEnergyHistoricalDataState,
    })
  ),

  on(
    EnergyHistoricalDataActions.clearLegendFeatures,
    (state): EnergyHistoricalDataState => ({
      ...state,
      legendFeatures: [],
    })
  ),

  on(
    EnergyHistoricalDataActions.loadLegacyHPChartData,
    (state): EnergyHistoricalDataState => ({
      ...state,
      loading: true,
    })
  ),

  on(
    EnergyHistoricalDataActions.loadLegacyHPChartDataSuccess,
    (state, { legacyChartData }): EnergyHistoricalDataState => {
      return {
        ...state,
        legacyChartData: mergeLegacyChartData(legacyChartData, state.legacyChartData),
        loading: false,
      };

      function mergeLegacyChartData(
        legacyData: ExtendedRawConsumptionProperties[],
        stateLegacyChartData: ExtendedRawConsumptionProperties[]
      ): ExtendedRawConsumptionProperties[] {
        if (!stateLegacyChartData) {
          return legacyData;
        }

        const additionalData = legacyData[0];
        let stateData = stateLegacyChartData[0];

        if (!stateData.week.value.length) {
          stateData = {
            ...stateData,
            week: {
              ...stateData.week,
              value: additionalData.week.value,
            },
          };
        }

        if (!stateData.month.value.length) {
          stateData = {
            ...stateData,
            month: {
              ...stateData.month,
              value: additionalData.month.value,
            },
          };
        }

        if (!stateData.year.value.length) {
          stateData = {
            ...stateData,
            year: {
              ...stateData.year,
              value: additionalData.year.value,
            },
          };
        }

        return [stateData];
      }
    }
  ),

  on(
    EnergyHistoricalDataActions.loadLegacyHPChartDataError,
    (state): EnergyHistoricalDataState => ({
      ...state,
      loading: false,
    })
  ),

  on(
    EnergyHistoricalDataActions.setHemsAnalysisTab,
    (state, { hemsAnalysisTab }): EnergyHistoricalDataState => ({
      ...state,
      hemsAnalysisSelectedTab: hemsAnalysisTab,
      loading: true,
    })
  ),

  on(
    EnergyHistoricalDataActions.saveSelectedFeatureToStorage,
    (
      state,
      { savedSelectedFeatures, installationId, isThermalFlow }
    ): EnergyHistoricalDataState => {
      const savedFeatures = { ...state.savedSelectedFeature?.[installationId] };
      if (isThermalFlow) {
        savedFeatures.heatingSelectedFeatures = savedSelectedFeatures;
      } else {
        savedFeatures.electricitySelectedFeatures = savedSelectedFeatures;
      }

      const objToSave = { ...state.savedSelectedFeature, [installationId]: savedFeatures };

      return {
        ...state,
        savedSelectedFeature: objToSave,
      };
    }
  ),

  on(
    EnergyHistoricalDataActions.loadSelectedFeatureFromStorage.success,
    (state, { savedSelectedFeature }): EnergyHistoricalDataState => ({
      ...state,
      savedSelectedFeature,
    })
  ),

  on(
    EnergyHistoricalDataActions.loadSelectedFeatureFromStorage.success,
    (state, { savedSelectedFeature }): EnergyHistoricalDataState => ({
      ...state,
      savedSelectedFeature,
    })
  )
);
